import type { OBB, Vec2, Vec3 } from '../../geo/types';
import { layoutToWorld } from '../../core/coords';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import type { ChunkSink } from '../mesh/chunker';
import type { Rng } from '../../core/rng';
import { edgeIdFor } from '../mesh/attributes';
import { SURFACE } from '../mesh/attributes';
import { SHIP_TEMPLATES, type ShipTemplate } from './shipTemplates';
import { hullStations, keelHeight } from './hull';
import { buildBoatShell } from './openBoat';
import type { HullPalette } from './hullParts';
import { timberBox } from './shipTimber';
import { framedBuilder } from './framedBuilder';
import { obbCorners } from '../../geo/polygon';
import type { ReservedFootprint } from '../fabric/types';
import { buildStocksHull, stocksFootprint, type StocksHullSpec } from './stocksHull';

/*
 * The hulls the woodcut draws on the stocks along the Lastage yard's shore (woodcut 880-1187 x
 * 3012-3097), read and placed by $S/ships-3/stocks/place.py (docs/plans/ships.md 15): the keel's
 * ends and the depth read on the woodcut in scan px, the length at the shared picture scale of
 * 0.1288 m a px (tools/quayGoods/sources/picture_scale.json) along the georeference's direction
 * there, the drawn middle through the woodcut georeference, then moved to the nearest yard ground
 * that holds the hull and its shores 0.6 m clear of the yard's edge, its goods
 * (lastageYard.yardReservations), its sheds, the rope walks and the streets. The woodcut draws
 * the yard's shore 20-30 m further out than the layout has it, so every hull moves ashore.
 */
export interface StocksHull {
  readonly id: string;
  readonly kind: 'hull' | 'boat';
  /** The drawn box in scan px of docs/reference/woodcut-1544-uva-8000px.jpg. */
  readonly woodcut: readonly [number, number, number, number];
  /** Layout ground under the drawn middle of the keel. */
  readonly drawn: Vec2;
  /** Layout ground under the middle of the keel as built. */
  readonly at: Vec2;
  /** Metres from `drawn` to `at`. */
  readonly shift: number;
  /** The keel's direction from stem to stern in the layout, radians. */
  readonly yaw: number;
  readonly spec: StocksHullSpec;
}

export const STOCKS_HULLS: readonly StocksHull[] = [
  {
    id: 'stocks-1',
    kind: 'hull',
    woodcut: [880, 3038, 968, 3084],
    drawn: [745.53, 36.17],
    at: [690.47, 29.18],
    shift: 55.5,
    yaw: 2.1776,
    spec: { length: 9.89, depth: 3.35, planked: 0.2 },
  },
  {
    id: 'stocks-2',
    kind: 'hull',
    woodcut: [977, 3012, 1087, 3076],
    drawn: [708.42, 72.87],
    at: [690.13, 70.13],
    shift: 18.5,
    yaw: 2.3229,
    spec: { length: 12.08, depth: 4.64, planked: 0.6 },
  },
  {
    id: 'stocks-boat',
    kind: 'boat',
    woodcut: [1103, 3025, 1147, 3052],
    drawn: [672.74, 99.36],
    at: [660.14, 92.18],
    shift: 14.5,
    yaw: 2.3074,
    spec: { length: 4.65, depth: 1.29, planked: 1 },
  },
  {
    id: 'stocks-3',
    kind: 'hull',
    woodcut: [1078, 3033, 1187, 3097],
    drawn: [681.71, 110.98],
    at: [657.76, 124.5],
    shift: 27.5,
    yaw: 2.4977,
    spec: { length: 12.02, depth: 3.35, planked: 0.5 },
  },
];

/** The boat's keel over the ground: the woodcut's blocks, 7 px under it. */
export const BOAT_BLOCKS = { height: 0.9, size: 0.3, at: [0.3, 0.5, 0.7] } as const;

/** Its own frame in the world: origin on the ground under the keel's middle, bow at -X. */
export function stocksFrame(hull: StocksHull): Frame {
  return { origin: layoutToWorld(hull.at, 0), yaw: hull.yaw };
}

/** The rowing boat's lines at the drawn length, its beam in proportion. */
export function stocksBoatTemplate(length: number): ShipTemplate {
  const rowing = SHIP_TEMPLATES.rowing;
  return { ...rowing, length, beam: (rowing.beam * length) / rowing.length };
}

function planSize(hull: StocksHull): { length: number; width: number } {
  if (hull.kind === 'hull') return stocksFootprint(hull.spec);
  const template = stocksBoatTemplate(hull.spec.length);
  return { length: template.length, width: template.beam };
}

/** The ground a hull and its shores stand on, `margin` wider all round, as a layout box. */
export function stocksObb(hull: StocksHull, margin = 0): OBB {
  const { length, width } = planSize(hull);
  return {
    center: hull.at,
    axisU: [Math.cos(hull.yaw), Math.sin(hull.yaw)],
    halfU: length / 2 + margin,
    halfV: width / 2 + margin,
  };
}

/* The lot planner keeps this far off a hull's ground, as it does off the yard's goods. */
const RESERVED_CLEARANCE = 0.5;

/** Each hull's ground and a walking gap round it, which no lot or shed may take. */
export function stocksReservations(): readonly ReservedFootprint[] {
  return STOCKS_HULLS.map((hull) => ({
    id: `stocks:${hull.id}`,
    polygon: { outer: obbCorners(stocksObb(hull, RESERVED_CLEARANCE)), holes: [] },
  }));
}

/** The ground a hull and its shores stand on, as a layout rectangle. */
export function stocksPlan(hull: StocksHull): readonly Vec2[] {
  const { length, width } = planSize(hull);
  const [c, s] = [Math.cos(hull.yaw), Math.sin(hull.yaw)];
  const corner = (along: number, across: number): Vec2 => [
    hull.at[0] + along * c - across * s,
    hull.at[1] + along * s + across * c,
  ];
  const [l, w] = [length / 2, width / 2];
  return [corner(-l, -w), corner(l, -w), corner(l, w), corner(-l, w)];
}

function buildBoatOnBlocks(
  builder: MeshBuilder,
  length: number,
  palette: HullPalette,
  edgeId: number,
  seed: number
): void {
  const template = stocksBoatTemplate(length);
  const lift = BOAT_BLOCKS.height + template.keelDepth;
  buildBoatShell(
    framedBuilder(builder, { origin: [0, lift, 0], yaw: 0 }),
    template,
    palette,
    edgeId,
    seed
  );
  const style: VertexStyle = {
    color: palette.hull,
    surface: SURFACE.wood,
    edgeId,
    seed,
    strokeAngle: 0,
    extent: [0, 0],
  };
  const b = BOAT_BLOCKS.size / 2;
  const [first] = hullStations(template);
  for (const s of BOAT_BLOCKS.at) {
    const x = first!.x + s * template.length;
    const top = lift + keelHeight(s, template.keelDepth);
    const min: Vec3 = [x - b, 0, -b];
    timberBox(builder, style, min, [x + b, top, b]);
  }
}

/** A hull or boat on the stocks, in its own frame (stocksFrame): bow at -X, the ground at y = 0. */
export function buildOnStocks(
  builder: MeshBuilder,
  hull: StocksHull,
  palette: HullPalette,
  edgeId: number,
  seed: number
): void {
  if (hull.kind === 'boat') {
    buildBoatOnBlocks(builder, hull.spec.length, palette, edgeId, seed);
    return;
  }
  const style: VertexStyle = {
    color: palette.hull,
    surface: SURFACE.wood,
    edgeId,
    seed,
    strokeAngle: 0,
    extent: [0, 0],
  };
  buildStocksHull(builder, hull.spec, style);
}

/**
 * Every hull on the stocks, drawn into the infrastructure chunk its keel stands in. Each is one
 * object to the outline pass, numbered after the palisades, whose ids come first. Returns how many.
 */
export function buildYardStocks(
  chunks: ChunkSink,
  palisades: number,
  palette: HullPalette,
  rng: Rng
): number {
  STOCKS_HULLS.forEach((hull, i) => {
    const builder = framedBuilder(chunks.builderAt('infrastructure', hull.at), stocksFrame(hull));
    buildOnStocks(builder, hull, palette, edgeIdFor(palisades + i), rng.next());
  });
  return STOCKS_HULLS.length;
}
